/**
 * Client – My coaching requests: list all sent requests grouped by status.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { getMyRequests, getCoach, cancelCoachingRequest } from '../../domain/usecases/coaching';
import Card from '../../shared/ui/Card.jsx';
import Avatar from '../../shared/ui/Avatar.jsx';
import Badge from '../../shared/ui/Badge.jsx';
import Button from '../../shared/ui/Button.jsx';
import Body from '../../shared/ui/Body.jsx';
import Title from '../../shared/ui/Title.jsx';
import Display from '../../shared/ui/Display.jsx';
import Label from '../../shared/ui/Label.jsx';
import EmptyState from '../../shared/ui/EmptyState.jsx';
import Skeleton from '../../shared/ui/Skeleton.jsx';
import styles from './MyRequestsPage.module.css';

function statusVariant(status) {
  if (status === 'pending') return 'gold';
  if (status === 'accepted') return 'success';
  if (status === 'declined' || status === 'rejected') return 'red';
  return 'neutral';
}

function formatTime(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : (value.toDate ? value.toDate() : new Date(value));
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const STATUS_ORDER = ['pending', 'accepted', 'declined'];
const STATUS_LABEL = { pending: 'Pending', accepted: 'Accepted', declined: 'Declined' };

export function MyRequestsPage() {
  const { userId } = useAuth();
  const [requests, setRequests] = useState([]);
  const [coaches, setCoaches] = useState({});
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);

  async function handleCancel(requestId) {
    if (!window.confirm('Cancel this coaching request?')) return;
    setCancellingId(requestId);
    try {
      await cancelCoachingRequest(requestId);
      setRequests((prev) => prev.filter((r) => r.id !== requestId));
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.message || 'Could not cancel request.');
    } finally {
      setCancellingId(null);
    }
  }

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    getMyRequests(userId)
      .then(async (reqs) => {
        setRequests(reqs);
        // Batch-fetch distinct coaches
        const uniqueCoachIds = [...new Set(reqs.map((r) => r.coachId).filter(Boolean))];
        const coachDocs = await Promise.all(uniqueCoachIds.map((id) => getCoach(id).catch(() => null)));
        const map = {};
        uniqueCoachIds.forEach((id, i) => { if (coachDocs[i]) map[id] = coachDocs[i]; });
        setCoaches(map);
      })
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) {
    return (
      <div className={styles.page}>
        <Display as="h1" size="l">My Requests</Display>
        <div className={styles.skeletonList}>
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} width="100%" height={72} radius="var(--radius-md)" />)}
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className={styles.page}>
        <Display as="h1" size="l">My Requests</Display>
        <EmptyState
          icon="users"
          title="No requests yet"
          body="Find a coach to send your first request."
          action={
            <Button variant="primary" as={Link} to="/coaching">
              Find a coach
            </Button>
          }
        />
      </div>
    );
  }

  // Group by status
  const grouped = {};
  STATUS_ORDER.forEach((s) => { grouped[s] = []; });
  requests.forEach((r) => {
    const key = STATUS_ORDER.includes(r.status) ? r.status : 'declined';
    grouped[key].push(r);
  });

  return (
    <div className={styles.page}>
      <Link to="/coaching" className={styles.back}>← Back to coaching</Link>
      <Display as="h1" size="l">My Requests</Display>

      {STATUS_ORDER.filter((s) => grouped[s].length > 0).map((status) => (
        <section key={status} className={styles.section}>
          <Title as="h2" className={styles.sectionTitle}>{STATUS_LABEL[status]}</Title>
          <div className={styles.list}>
            {grouped[status].map((req) => {
              const coach = coaches[req.coachId];
              const coachName = coach?.name || req.coachId;
              return (
                <Card key={req.id} padding="sm" className={styles.row}>
                  <div className={styles.rowInner}>
                    <Avatar
                      size="sm"
                      src={coach?.profileImage}
                      initials={coachName.charAt(0)}
                      alt={coachName}
                    />
                    <div className={styles.rowMeta}>
                      <div className={styles.rowTop}>
                        <Body as="span" style={{ fontWeight: 600 }}>{coachName}</Body>
                        <Badge variant={statusVariant(req.status)}>{req.status}</Badge>
                      </div>
                      {req.status === 'declined' && req.reason && (
                        <Body tone="secondary" as="p" className={styles.reason}>
                          <Label>REASON</Label> {req.reason}
                        </Body>
                      )}
                      <Body tone="tertiary" as="p" className={styles.timestamp}>
                        {formatTime(req.createdAt)}
                      </Body>
                      {req.status === 'pending' && (
                        <div className={styles.rowActions}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancel(req.id)}
                            disabled={cancellingId === req.id}
                          >
                            {cancellingId === req.id ? 'Cancelling…' : 'Cancel request'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
